import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldStar, ArrowLeft, Lock } from "@phosphor-icons/react";
import { TEST_IDS } from "@/lib/testIds";

function formatDetail(detail) {
  if (detail == null) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail.msg ?? detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      if (data.session_token) localStorage.setItem("nk_session_token", data.session_token);
      setUser(data.user);
      const role = data.user.role;
      const isAdmin = role === "super_admin" || role === "sub_admin" || data.user.is_admin;
      if (!isAdmin) {
        toast.error("This account doesn't have admin access.");
        await api.post("/auth/logout").catch(() => {});
        localStorage.removeItem("nk_session_token");
        setUser(null);
        setBusy(false);
        return;
      }
      toast.success(`Welcome, ${data.user.name || "Admin"}`);
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-nk-bg text-nk-text flex flex-col">
      <header className="border-b border-nk-line/60">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-nk-mute hover:text-nk-text">
            <ArrowLeft size={14} weight="bold" />
            Back to NKognito
          </Link>
          <div className="inline-flex items-center gap-2 text-xs text-nk-dim uppercase tracking-widest">
            <Lock size={12} weight="bold" /> Admin Portal
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-nk-accent/10 border border-nk-accent/30 mb-4">
              <ShieldStar size={22} weight="duotone" className="text-nk-accent" />
            </div>
            <p className="eyebrow mb-2">Admin Portal</p>
            <h1 className="font-display text-4xl font-medium tracking-tight">Operator sign-in</h1>
            <p className="text-sm text-nk-mute mt-3">
              Restricted access. Use your admin credentials.
            </p>
          </div>

          <form onSubmit={submit} className="nk-card p-6 space-y-3">
            <input
              data-testid={TEST_IDS.authEmailInput}
              type="email" required
              placeholder="admin@nkognito.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-nk-bg border border-nk-line px-3 py-2.5 text-sm rounded-sm focus:border-nk-accent outline-none transition-colors"
              autoComplete="email"
            />
            <input
              data-testid={TEST_IDS.authPasswordInput}
              type="password" required minLength={8}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-nk-bg border border-nk-line px-3 py-2.5 text-sm rounded-sm focus:border-nk-accent outline-none transition-colors"
              autoComplete="current-password"
            />
            <button
              data-testid={TEST_IDS.adminLoginBtn}
              disabled={busy}
              className="w-full bg-nk-accent text-nk-bg font-medium px-4 py-2.5 rounded-sm hover:bg-nk-accentSoft disabled:opacity-40 transition-colors"
            >
              {busy ? "Authenticating..." : "Sign in to admin"}
            </button>
          </form>

          <p className="text-[11px] text-nk-dim mt-4 text-center leading-relaxed">
            Player account? Use the regular sign-in button on the homepage.
          </p>
        </div>
      </main>
    </div>
  );
}
